/**
 * v2 Settings → My settings — per-operator preferences.
 *
 * Theme picker (light/dark/system), local-cache clear, session ID
 * read-out, legacy-UI fallback link. Theme + future prefs are
 * persisted server-side via ``/api/user/preferences`` with
 * localStorage as the write-through cache.
 */
import { useQueryClient } from "@tanstack/react-query";
import { ExternalLink, Moon, RotateCw, Sun, Wand2 } from "lucide-react";

import { useSession } from "../../../auth/SessionContext";
import { Badge } from "../../components/Badge";
import { CopyableCell } from "../../components/CopyableCell";
import { useToast } from "../../components/Toast";
import { HelpButton } from "../../help/HelpPanel";
import { useTheme, type ThemeChoice } from "../../layout/ThemeProvider";

const THEME_CHOICES: { value: ThemeChoice; label: string; icon: React.ReactNode }[] = [
  { value: "light", label: "Light", icon: <Sun className="h-3.5 w-3.5" /> },
  { value: "dark", label: "Dark", icon: <Moon className="h-3.5 w-3.5" /> },
  { value: "system", label: "System", icon: <Wand2 className="h-3.5 w-3.5" /> },
];

export function MySettings() {
  const { user } = useSession();
  const { theme, resolved, setTheme } = useTheme();
  const qc = useQueryClient();
  const toast = useToast();

  const onClearCache = () => {
    qc.clear();
    toast.push("Local query cache cleared", "success");
  };

  const onLegacyUi = () => {
    // Set the localStorage flag so the router can redirect /chassis
    // back to v1 for this operator until they clear it.
    try {
      window.localStorage.setItem("prefer_v1_until", String(Date.now() + 24 * 3600 * 1000));
    } catch {
      /* ignore */
    }
  };

  return (
    <section className="flex flex-col gap-4">
      <header>
        <div className="flex items-center gap-2">
          <h1 className="text-lg font-semibold text-slate-100">My settings</h1>
          <HelpButton topic="settings-me" />
        </div>
        <p className="text-xs text-slate-500">
          Operator-scoped preferences. Stored server-side and
          mirrored to browser localStorage for instant-load on
          subsequent visits.
        </p>
      </header>

      {/* Theme */}
      <section className="rounded-lg bg-slate-900 shadow-border p-4">
        <h2 className="mb-2 text-sm font-medium text-slate-200">Theme</h2>
        <div className="flex items-center gap-2">
          {THEME_CHOICES.map((c) => (
            <button
              key={c.value}
              type="button"
              onClick={() => setTheme(c.value)}
              className={[
                "inline-flex items-center gap-1.5 rounded border px-3 py-1.5 text-sm",
                theme === c.value
                  ? "border-emerald-700 bg-emerald-900/40 text-emerald-200"
                  : "border-slate-700 bg-slate-800 text-slate-300 hover:bg-slate-700",
              ].join(" ")}
            >
              {c.icon}
              {c.label}
            </button>
          ))}
          <span className="ml-2 text-xs text-slate-500">
            currently rendering <Badge tone="info">{resolved}</Badge>
          </span>
        </div>
      </section>

      {/* Cache */}
      <section className="rounded-lg bg-slate-900 shadow-border p-4">
        <h2 className="mb-2 text-sm font-medium text-slate-200">Local cache</h2>
        <p className="mb-2 text-xs text-slate-500">
          React Query holds the responses of every recent API call so
          pages snap-load on the second visit. Clear if you suspect
          a stale view of the world.
        </p>
        <button
          type="button"
          onClick={onClearCache}
          className="inline-flex items-center gap-1 rounded border border-slate-700 bg-slate-800 px-2 py-1 text-xs text-slate-200 hover:bg-slate-700"
        >
          <RotateCw className="h-3 w-3" />
          Clear local query cache
        </button>
      </section>

      {/* Session */}
      <section className="rounded-lg bg-slate-900 shadow-border p-4">
        <h2 className="mb-2 text-sm font-medium text-slate-200">Session</h2>
        <dl className="grid grid-cols-2 gap-2 text-xs">
          <dt className="text-slate-500">Username</dt>
          <dd className="text-slate-200">{user?.gigafy_username ?? "—"}</dd>
          <dt className="text-slate-500">Session cookie</dt>
          <dd className="font-mono text-slate-200">
            <CopyableCell value={readCookie("sid")} display={shorten(readCookie("sid"))} />
          </dd>
          <dt className="text-slate-500">CSRF token</dt>
          <dd className="font-mono text-slate-200">
            <CopyableCell value={readCookie("csrf")} display={shorten(readCookie("csrf"))} />
          </dd>
        </dl>
      </section>

      {/* Legacy UI fallback */}
      <section className="rounded-lg bg-slate-900 shadow-border p-4">
        <h2 className="mb-2 text-sm font-medium text-slate-200">Legacy UI</h2>
        <p className="mb-2 text-xs text-slate-500">
          The v1.0 UI is still available if you need a workflow that
          hasn't fully landed in v2 yet. Opening it here doesn't
          force-stick to v1 — the next visit returns to v2 unless
          you opt-in.
        </p>
        <a
          href="/chassis"
          onClick={onLegacyUi}
          className="inline-flex items-center gap-1 rounded border border-amber-700 bg-amber-900/30 px-3 py-1.5 text-xs text-amber-200 hover:bg-amber-900/50"
        >
          <ExternalLink className="h-3 w-3" />
          Open legacy UI (v1.0)
        </a>
      </section>
    </section>
  );
}

function readCookie(name: string): string {
  if (typeof document === "undefined") return "";
  const match = document.cookie.match(new RegExp(`(^|;\\s*)${name}=([^;]+)`));
  return match ? decodeURIComponent(match[2]) : "";
}

function shorten(value: string): string {
  if (!value) return "(not set)";
  if (value.length <= 16) return value;
  return value.slice(0, 8) + "…" + value.slice(-4);
}
